import React, { useState } from "react";
import "./Sidebar.css";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import MobileNavigation from "../../components/common/MobileNavigation";
import {
  Home,
  FileText,
  BarChart3,
  Settings,
  Users,
  DollarSign,
  CheckCircle,
  Clock,
} from "lucide-react";

const Sidebar = ({ children }) => {
  const accessRole = useSelector((state) => state.user.accessRole).trim();
  const department = useSelector((state) => state.user.user.department);
  const accessRoles = accessRole.split("#");
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isJeff = accessRoles.includes("isJeff");

  // Navigation items
  const navigationItems = [
    {
      label: "Home",
      path: "/",
      icon: Home,
    },
    {
      label: "Project Details",
      path: "/reconciliation-dash",
      icon: FileText,
      children: [
        {
          label: "Projects Dashboard",
          path: "/reconciliation-dash",
          icon: BarChart3,
        },
      ],
    },
    {
      label: "Plot Discharge",
      path: "/discharge-approvals",
      icon: CheckCircle,
      children: [
        {
          label: "Level 1 Processing",
          path: "/discharge-approvals",
          icon: CheckCircle,
        },
        {
          label: "Level 2 Processing",
          path: "/discharge-level2",
          icon: CheckCircle,
        },
        {
          label: "Legal Processing",
          path: "/discharge-legal",
          icon: CheckCircle,
        },
        {
          label: "View Discharged Plots",
          path: "/discharge-initiation",
          icon: Clock,
        },
      ],
    },
    {
      label: "Reports",
      path: "/reports",
      icon: BarChart3,
    },
  ];

  const user = {
    name: useSelector((state) => state.user.user.name) || "User",
    email: useSelector((state) => state.user.user.email) || "user@example.com",
  };

  return (
    <>
      <div className="drawer lg:drawer-open">
        <input id="my-drawer" type="checkbox" className="drawer-toggle" />
        <div className="drawer-content flex flex-col">
          {/* Mobile header */}
          <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <h1 className="text-lg font-semibold text-gray-900">
                Discharge Reconciliation
              </h1>
              <MobileNavigation
                items={navigationItems}
                user={user}
                onLogout={() => {
                  // Handle logout
                  console.log("Logout clicked");
                }}
              />
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 overflow-visible">{children}</div>
        </div>

        {/* Desktop sidebar */}
        <div className="drawer-side hidden lg:block">
          <label htmlFor="my-drawer" className="drawer-overlay"></label>
          <div className="min-h-full w-80 bg-white shadow-lg">
            {/* Sidebar header */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Discharge Reconciliation
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Project Management System
              </p>
            </div>

            {/* Navigation */}
            <nav className="p-4 space-y-2">
              {/* Home */}
              <Link
                to="/"
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  location.pathname === "/"
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <Home className="mr-3 h-5 w-5" />
                Home
              </Link>

              {/* Project Details */}
              {(isJeff || department === "ICT SYSTEMS") && (
                <div className="space-y-1">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Project Details
                  </div>
                  <Link
                    to="/reconciliation-dash"
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === "/reconciliation-dash"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <BarChart3 className="mr-3 h-5 w-5" />
                    Projects Dashboard
                  </Link>
                </div>
              )}

              {/* Plot Discharge */}
              {(isJeff || department === "ICT SYSTEMS") && (
                <div className="space-y-1">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Plot Discharge
                  </div>
                  <Link
                    to="/discharge-approvals"
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === "/discharge-approvals"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <CheckCircle className="mr-3 h-5 w-5" />
                    Level 1 Processing
                  </Link>
                  <Link
                    to="/discharge-level2"
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === "/discharge-level2"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <CheckCircle className="mr-3 h-5 w-5" />
                    Level 2 Processing
                  </Link>
                  <Link
                    to="/discharge-legal"
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === "/discharge-legal"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <CheckCircle className="mr-3 h-5 w-5" />
                    Legal Processing
                  </Link>
                  <Link
                    to="/discharge-initiation"
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === "/discharge-initiation"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <Clock className="mr-3 h-5 w-5" />
                    View Discharged Plots
                  </Link>
                </div>
              )}

              {/* Reports */}
              {(isJeff || department === "ICT SYSTEMS") && (
                <div className="space-y-1">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Reports
                  </div>
                  <Link
                    to="/reports"
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === "/reports"
                        ? "bg-blue-100 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }`}
                  >
                    <BarChart3 className="mr-3 h-5 w-5" />
                    Analytics & Reports
                  </Link>
                </div>
              )}
            </nav>

            {/* User info */}
            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                    <Users className="h-5 w-5 text-gray-600" />
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    {user.name}
                  </p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
